import type {
  FinchHrisGoldSignalRow,
  FinchHrisGoldSignalsArtifactV1,
} from './integrations/finch-hris';

export const GOVERNED_JOIN_SPEC_SCHEMA_VERSION = 'governed_join_spec_v1' as const;

export type GovernedJoinKind = 'reconciliation';
export type GovernedJoinFactFamily = 'governed_metric_fact';
export type GovernedJoinConnectorFamily = 'finch_hris_gold_signals';
export type GovernedJoinFindingSeverity = 'INFO' | 'WARN' | 'ERROR';

export type GovernedJoinTemporalGrain =
  | Readonly<{ kind: 'period'; periodType: string }>
  | Readonly<{ kind: 'point_in_time' }>;

/**
 * Cross-lake never-blend rule: a period fact may only join to a point-in-time
 * connector mirror fact when the spec names how the point-in-time cut represents
 * the period. Without this declaration, the join must refuse.
 */
export type GovernedJoinAsOfSemantics = Readonly<{
  kind: 'period_end_snapshot';
  periodType: string;
  description: string;
}>;

export type GovernedJoinMetricFactFamilySpec = Readonly<{
  family: GovernedJoinFactFamily;
  metricKeys: readonly string[];
  temporalGrain: GovernedJoinTemporalGrain;
}>;

export type GovernedJoinConnectorMirrorFamilySpec = Readonly<{
  family: GovernedJoinConnectorFamily;
  signalKeys: readonly string[];
  temporalGrain: GovernedJoinTemporalGrain;
}>;

export type GovernedJoinDimensionMapping = Readonly<{
  dimension: string;
  leftField: 'businessLineKey';
  rightDimensionKey: string;
  required: true;
}>;

export type GovernedJoinPeriodKey = Readonly<{
  leftPeriodType: string;
  rightAsOfPolicy: 'explicit_period_end_snapshot';
}>;

export type GovernedJoinKeySpec = Readonly<{
  period: GovernedJoinPeriodKey;
  dimensions: readonly GovernedJoinDimensionMapping[];
}>;

export type GovernedJoinGrainRules = Readonly<{
  neverBlendDimensions: readonly string[];
  asOfSemantics: GovernedJoinAsOfSemantics | null;
}>;

export type GovernedJoinSpec = Readonly<{
  schemaVersion: typeof GOVERNED_JOIN_SPEC_SCHEMA_VERSION;
  joinId: string;
  joinKind: GovernedJoinKind;
  leftFacts: GovernedJoinMetricFactFamilySpec;
  rightSource: GovernedJoinConnectorMirrorFamilySpec;
  joinKey: GovernedJoinKeySpec;
  grainRules: GovernedJoinGrainRules;
  outputMetricKey: string;
  description: string;
}>;

export type GovernedJoinValidationReason =
  | 'invalid_contract'
  | 'missing_join_key'
  | 'temporal_grain_mismatch'
  | 'never_blend_dimension_unmapped';

export type GovernedJoinValidationAccepted = Readonly<{ ok: true }>;

export type GovernedJoinValidationRefused = Readonly<{
  ok: false;
  reason: GovernedJoinValidationReason;
  message: string;
  details?: Readonly<Record<string, unknown>>;
}>;

export type GovernedJoinValidationResult =
  | GovernedJoinValidationAccepted
  | GovernedJoinValidationRefused;

export type GovernedJoinMetricFactLike = Readonly<{
  id: string;
  tenantId: string;
  metricKey: string;
  businessLineKey?: string | null;
  geoKey?: string | null;
  customerKey?: string | null;
  laneKey?: string | null;
  siteKey?: string | null;
  periodType: string;
  periodKey: string;
  valueNumeric: number | string;
  unit: string;
  sourceClass: string;
  sourceSystemRef: string;
  evidenceRefId: string;
  artifactId: string;
  ingestJobId: string;
  asOf: string | Date;
  approvedAt?: string | Date | null;
}>;

export type GovernedJoinParentRef = Readonly<{
  family: 'governed_metric_fact' | 'connector_mirror_fact';
  refId: string;
  evidenceRefId: string;
  artifactId: string;
  ingestJobId?: string | null;
  sourceFingerprint?: string | null;
}>;

export type GovernedJoinRow = Readonly<{
  joinId: string;
  tenantId: string;
  joinKey: Readonly<{
    periodType: string;
    periodKey: string;
    businessLineKey: string;
    rightAsOf: string;
  }>;
  leftValue: number;
  rightValue: number;
  delta: number;
  unit: 'count';
  parents: readonly [GovernedJoinParentRef, GovernedJoinParentRef];
}>;

export type GovernedJoinFinding = Readonly<{
  severity: GovernedJoinFindingSeverity;
  code: 'reporting_headcount_reconciliation_delta';
  message: string;
  joinKey: GovernedJoinRow['joinKey'];
  delta: number;
  leftValue: number;
  rightValue: number;
  parentRefIds: readonly [string, string];
}>;

export type GovernedJoinPromotedMetricFactCandidate = Readonly<{
  id: string;
  tenantId: string;
  metricKey: string;
  businessLineKey: string;
  geoKey: null;
  customerKey: null;
  laneKey: null;
  siteKey: null;
  periodType: string;
  periodKey: string;
  valueNumeric: number;
  unit: 'count_delta';
  sourceClass: 'governed_join';
  sourceSystemRef: string;
  evidenceRefId: string;
  artifactId: string;
  ingestJobId: string;
  asOf: string;
  /**
   * A3-style promotion lineage. The promoted fact has its own ingest job, while
   * this provenance preserves both source parents across the finance and
   * connector lakes.
   */
  provenance: Readonly<{
    joinId: string;
    joinKind: GovernedJoinKind;
    parents: readonly [GovernedJoinParentRef, GovernedJoinParentRef];
  }>;
}>;

export type GovernedJoinAcceptedResult = Readonly<{
  ok: true;
  spec: GovernedJoinSpec;
  rows: readonly GovernedJoinRow[];
  findings: readonly GovernedJoinFinding[];
  promotedFacts: readonly GovernedJoinPromotedMetricFactCandidate[];
}>;

export type GovernedJoinRefusedResult = Readonly<{
  ok: false;
  reason:
    | GovernedJoinValidationReason
    | 'as_of_mismatch'
    | 'duplicate_join_key'
    | 'missing_left_fact'
    | 'missing_right_fact'
    | 'scope_mismatch';
  message: string;
  details?: Readonly<Record<string, unknown>>;
}>;

export type GovernedJoinResult = GovernedJoinAcceptedResult | GovernedJoinRefusedResult;

export type ReportingHeadcountFinchReconciliationInput = Readonly<{
  spec: GovernedJoinSpec;
  leftFacts: readonly GovernedJoinMetricFactLike[];
  rightArtifact: FinchHrisGoldSignalsArtifactV1;
  rightScope: Readonly<{
    orgStateId: string;
  }>;
  promotion: Readonly<{
    outputMetricKey: string;
    artifactId: string;
    ingestJobId: string;
    generatedAt: string;
  }>;
}>;

export const FINCH_REPORTING_HEADCOUNT_RECONCILIATION_JOIN_SPEC: GovernedJoinSpec = {
  schemaVersion: GOVERNED_JOIN_SPEC_SCHEMA_VERSION,
  joinId: 'finch_reporting_headcount_reconciliation_v1',
  joinKind: 'reconciliation',
  leftFacts: {
    family: 'governed_metric_fact',
    metricKeys: ['reporting_headcount'],
    temporalGrain: { kind: 'period', periodType: 'fiscal_year' },
  },
  rightSource: {
    family: 'finch_hris_gold_signals',
    signalKeys: ['hris.positions.mapped.total'],
    temporalGrain: { kind: 'point_in_time' },
  },
  joinKey: {
    period: {
      leftPeriodType: 'fiscal_year',
      rightAsOfPolicy: 'explicit_period_end_snapshot',
    },
    dimensions: [
      {
        dimension: 'business_line',
        leftField: 'businessLineKey',
        rightDimensionKey: 'businessLineKey',
        required: true,
      },
    ],
  },
  grainRules: {
    neverBlendDimensions: ['business_line'],
    asOfSemantics: {
      kind: 'period_end_snapshot',
      periodType: 'fiscal_year',
      description:
        'Finance reporting_headcount is a fiscal-year period fact. Finch mirror headcount is admitted only as an explicit fiscal-year-end snapshot; absent that asOf declaration, annual finance and point-in-time HRIS facts must not be joined.',
    },
  },
  outputMetricKey: 'reporting_headcount_reconciliation_delta',
  description:
    'Reconciles finance-declared reporting_headcount to Finch mirror-derived mapped position headcount by fiscal year and business line.',
};

export function validateGovernedJoinSpec(spec: unknown): GovernedJoinValidationResult {
  if (!isRecord(spec)) {
    return refused('invalid_contract', 'GovernedJoinSpec must be an object.');
  }

  if (spec.schemaVersion !== GOVERNED_JOIN_SPEC_SCHEMA_VERSION) {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec schemaVersion must be governed_join_spec_v1.'
    );
  }

  if (spec.joinKind !== 'reconciliation') {
    return refused('invalid_contract', 'GovernedJoinSpec joinKind must be reconciliation.');
  }

  if (!nonEmptyString(spec.joinId) || !nonEmptyString(spec.outputMetricKey)) {
    return refused('invalid_contract', 'GovernedJoinSpec joinId and outputMetricKey are required.');
  }

  const leftFacts = isRecord(spec.leftFacts) ? spec.leftFacts : null;
  if (leftFacts?.family !== 'governed_metric_fact') {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec leftFacts.family must be governed_metric_fact.'
    );
  }
  if (!hasNonEmptyStringArray(leftFacts.metricKeys)) {
    return refused('invalid_contract', 'GovernedJoinSpec leftFacts.metricKeys must be non-empty.');
  }

  const rightSource = isRecord(spec.rightSource) ? spec.rightSource : null;
  if (rightSource?.family !== 'finch_hris_gold_signals') {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec rightSource.family must be finch_hris_gold_signals.'
    );
  }
  if (!hasNonEmptyStringArray(rightSource.signalKeys)) {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec rightSource.signalKeys must be non-empty.'
    );
  }

  const joinKey = isRecord(spec.joinKey) ? spec.joinKey : null;
  const period = isRecord(joinKey?.period) ? joinKey.period : null;
  if (!period || !nonEmptyString(period.leftPeriodType)) {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec joinKey.period.leftPeriodType is required.'
    );
  }
  if (period.rightAsOfPolicy !== 'explicit_period_end_snapshot') {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec joinKey.period.rightAsOfPolicy must be explicit_period_end_snapshot.'
    );
  }

  const dimensions = Array.isArray(joinKey?.dimensions) ? joinKey.dimensions : [];
  if (dimensions.length === 0) {
    return refused(
      'missing_join_key',
      'GovernedJoinSpec joinKey must declare at least one dimension mapping.'
    );
  }

  const grainRules = isRecord(spec.grainRules) ? spec.grainRules : null;
  if (!grainRules) {
    return refused('invalid_contract', 'GovernedJoinSpec grainRules are required.');
  }

  const neverBlendDimensions = Array.isArray(grainRules?.neverBlendDimensions)
    ? grainRules.neverBlendDimensions.map((dimension) => String(dimension))
    : [];

  for (const dimension of neverBlendDimensions) {
    const mapped = dimensions.some(
      (mapping) => isRecord(mapping) && mapping.dimension === dimension && mapping.required === true
    );
    if (!mapped) {
      return refused(
        'never_blend_dimension_unmapped',
        `Never-blend dimension ${dimension} must have an explicit cross-lake join mapping.`,
        { dimension }
      );
    }
  }

  const leftTemporal = readTemporalKind(spec.leftFacts);
  const rightTemporal = readTemporalKind(spec.rightSource);
  if (!leftTemporal || !rightTemporal) {
    return refused('invalid_contract', 'GovernedJoinSpec temporal grains are required.');
  }

  if (
    leftTemporal &&
    rightTemporal &&
    leftTemporal !== rightTemporal &&
    !isRecord(grainRules?.asOfSemantics)
  ) {
    return refused(
      'temporal_grain_mismatch',
      'Cross-lake joins with different temporal grains require explicit asOf semantics; annual finance and point-in-time connector mirror facts must not be blended silently.',
      { leftTemporal, rightTemporal }
    );
  }

  const asOfSemantics = isRecord(grainRules.asOfSemantics) ? grainRules.asOfSemantics : null;
  if (
    leftTemporal === 'period' &&
    rightTemporal === 'point_in_time' &&
    asOfSemantics &&
    asOfSemantics.kind !== 'period_end_snapshot'
  ) {
    return refused(
      'temporal_grain_mismatch',
      'Period-to-point cross-lake joins currently require period_end_snapshot asOf semantics.',
      { leftTemporal, rightTemporal }
    );
  }

  if (
    leftTemporal === 'period' &&
    rightTemporal === 'point_in_time' &&
    asOfSemantics?.periodType !== period.leftPeriodType
  ) {
    return refused(
      'invalid_contract',
      'GovernedJoinSpec asOfSemantics.periodType must match joinKey.period.leftPeriodType.',
      {
        asOfSemanticsPeriodType: asOfSemantics?.periodType,
        leftPeriodType: period.leftPeriodType,
      }
    );
  }

  return { ok: true };
}

export function reconcileReportingHeadcountWithFinchMirror(
  input: ReportingHeadcountFinchReconciliationInput
): GovernedJoinResult {
  const validation = validateGovernedJoinSpec(input.spec);
  if (!validation.ok) return validation;

  const mapping = input.spec.joinKey.dimensions.find((item) => item.dimension === 'business_line');
  if (!mapping) {
    return refused(
      'never_blend_dimension_unmapped',
      'Business-line never-blend mapping is required for reporting headcount reconciliation.'
    );
  }

  const facts = input.leftFacts
    .filter((fact) => input.spec.leftFacts.metricKeys.includes(fact.metricKey))
    .filter((fact) => fact.periodType === input.spec.joinKey.period.leftPeriodType)
    .slice()
    .sort(compareFacts);
  if (facts.length === 0) {
    return refused(
      'missing_left_fact',
      'No finance reporting_headcount facts matched the join spec.'
    );
  }

  const duplicateLeftKey = firstDuplicate(facts, (fact) => leftJoinKey(fact));
  if (duplicateLeftKey) {
    return refused(
      'duplicate_join_key',
      'Duplicate finance reporting_headcount facts share the same governed join key.',
      { joinKey: duplicateLeftKey }
    );
  }

  const tenantId = facts[0].tenantId;
  const mixedTenant = facts.find((fact) => fact.tenantId !== tenantId);
  if (mixedTenant) {
    return refused('scope_mismatch', 'Finance facts for a governed join must share one tenant.', {
      tenantId,
      mismatchedTenantId: mixedTenant.tenantId,
    });
  }

  const periodKey = facts[0].periodKey;
  const mixedPeriod = facts.find((fact) => fact.periodKey !== periodKey);
  if (mixedPeriod) {
    return refused(
      'as_of_mismatch',
      'A point-in-time Finch artifact can only reconcile one fiscal-year period at a time.',
      {
        periodKey,
        mismatchedPeriodKey: mixedPeriod.periodKey,
      }
    );
  }

  const expectedRightAsOf = periodEndAsOf(input.spec.joinKey.period.leftPeriodType, periodKey);
  if (!expectedRightAsOf || normalizeIso(input.rightArtifact.asOf) !== expectedRightAsOf) {
    return refused(
      'as_of_mismatch',
      'Finch mirror artifact asOf must match the explicit fiscal-year period-end snapshot for this join.',
      {
        expectedRightAsOf,
        actualRightAsOf: input.rightArtifact.asOf,
        periodKey,
      }
    );
  }

  if (input.rightArtifact.scope.tenantId !== tenantId) {
    return refused(
      'scope_mismatch',
      'Finch mirror artifact tenantId must match the finance governed metric fact tenantId.',
      {
        leftTenantId: tenantId,
        rightTenantId: input.rightArtifact.scope.tenantId,
      }
    );
  }

  if (input.rightArtifact.scope.orgStateId !== input.rightScope.orgStateId) {
    return refused(
      'scope_mismatch',
      'Finch mirror artifact orgStateId must match the explicitly bound rightScope.orgStateId.',
      {
        expectedOrgStateId: input.rightScope.orgStateId,
        actualOrgStateId: input.rightArtifact.scope.orgStateId,
      }
    );
  }

  const signals = input.rightArtifact.signals
    .filter((signal) => input.spec.rightSource.signalKeys.includes(signal.key))
    .filter((signal) => signal.unit === 'count')
    .slice()
    .sort(compareSignals);
  if (signals.length === 0) {
    return refused(
      'missing_right_fact',
      'No Finch mirror headcount signals matched the join spec.'
    );
  }

  const sourceAsOfMismatch = input.rightArtifact.sources.find(
    (source) => normalizeIso(source.asOf) !== expectedRightAsOf
  );
  if (sourceAsOfMismatch) {
    return refused(
      'as_of_mismatch',
      'Finch mirror source asOf must match the explicit fiscal-year period-end snapshot for this join.',
      {
        expectedRightAsOf,
        actualSourceAsOf: sourceAsOfMismatch.asOf,
      }
    );
  }

  const signalByBusinessLine = new Map<string, FinchHrisGoldSignalRow>();
  for (const signal of signals) {
    const businessLineKey = readDimensionValue(signal, mapping.rightDimensionKey);
    if (!businessLineKey) {
      return refused(
        'missing_right_fact',
        'Finch mirror headcount signal is missing businessLineKey.'
      );
    }
    if (normalizeIso(signal.evidence.asOf) !== expectedRightAsOf) {
      return refused(
        'as_of_mismatch',
        'Finch mirror signal evidence asOf must match the explicit fiscal-year period-end snapshot for this join.',
        {
          businessLineKey,
          expectedRightAsOf,
          actualEvidenceAsOf: signal.evidence.asOf,
        }
      );
    }
    if (signalByBusinessLine.has(businessLineKey)) {
      return refused(
        'duplicate_join_key',
        'Duplicate Finch mirror headcount signals share the same governed join key.',
        { businessLineKey }
      );
    }
    signalByBusinessLine.set(businessLineKey, signal);
  }

  const rows: GovernedJoinRow[] = [];
  for (const fact of facts) {
    const businessLineKey = nonEmptyString(fact.businessLineKey);
    if (!businessLineKey) {
      return refused(
        'missing_left_fact',
        'Finance reporting_headcount fact is missing businessLineKey.',
        {
          factId: fact.id,
        }
      );
    }

    const signal = signalByBusinessLine.get(businessLineKey);
    if (!signal) {
      return refused(
        'missing_right_fact',
        'Finch mirror headcount signal is missing for business line.',
        {
          businessLineKey,
        }
      );
    }

    const leftValue = finiteNumber(fact.valueNumeric);
    const rightValue = finiteNumber(signal.value);
    if (leftValue === null) {
      return refused(
        'missing_left_fact',
        'Finance reporting_headcount fact has no numeric value.',
        {
          factId: fact.id,
        }
      );
    }
    if (rightValue === null) {
      return refused('missing_right_fact', 'Finch mirror headcount signal has no numeric value.', {
        businessLineKey,
      });
    }

    const leftParent = parentFromMetricFact(fact);
    const rightParent = parentFromFinchSignal(signal);
    rows.push({
      joinId: input.spec.joinId,
      tenantId: fact.tenantId,
      joinKey: {
        periodType: fact.periodType,
        periodKey: fact.periodKey,
        businessLineKey,
        rightAsOf: input.rightArtifact.asOf,
      },
      leftValue,
      rightValue,
      delta: leftValue - rightValue,
      unit: 'count',
      parents: [leftParent, rightParent],
    });
  }

  const findings = rows
    .filter((row) => row.delta !== 0)
    .map((row): GovernedJoinFinding => {
      const [leftParent, rightParent] = row.parents;
      return {
        severity: 'INFO',
        code: 'reporting_headcount_reconciliation_delta',
        message:
          `Finance reporting_headcount and Finch mirror headcount differ by ${row.delta} for ` +
          `${row.joinKey.businessLineKey} in ${row.joinKey.periodKey}.`,
        joinKey: row.joinKey,
        delta: row.delta,
        leftValue: row.leftValue,
        rightValue: row.rightValue,
        parentRefIds: [leftParent.refId, rightParent.refId],
      };
    })
    .sort((left, right) =>
      left.joinKey.businessLineKey.localeCompare(right.joinKey.businessLineKey)
    );

  const promotedFacts = rows.map((row) =>
    promoteRowToGovernedMetricFactCandidate(row, input.spec, input.promotion)
  );

  return {
    ok: true,
    spec: input.spec,
    rows,
    findings,
    promotedFacts,
  };
}

function promoteRowToGovernedMetricFactCandidate(
  row: GovernedJoinRow,
  spec: GovernedJoinSpec,
  promotion: ReportingHeadcountFinchReconciliationInput['promotion']
): GovernedJoinPromotedMetricFactCandidate {
  return {
    id: [
      'governed_join_fact',
      spec.joinId,
      promotion.outputMetricKey,
      row.joinKey.businessLineKey,
      row.joinKey.periodType,
      row.joinKey.periodKey,
    ].join(':'),
    tenantId: row.tenantId,
    metricKey: promotion.outputMetricKey,
    businessLineKey: row.joinKey.businessLineKey,
    geoKey: null,
    customerKey: null,
    laneKey: null,
    siteKey: null,
    periodType: row.joinKey.periodType,
    periodKey: row.joinKey.periodKey,
    valueNumeric: row.delta,
    unit: 'count_delta',
    sourceClass: 'governed_join',
    sourceSystemRef: `governed_join:${spec.joinId}`,
    evidenceRefId:
      `governed_join:${spec.joinId}:${row.joinKey.periodType}:${row.joinKey.periodKey}:` +
      row.joinKey.businessLineKey,
    artifactId: promotion.artifactId,
    ingestJobId: promotion.ingestJobId,
    asOf: promotion.generatedAt,
    provenance: {
      joinId: spec.joinId,
      joinKind: spec.joinKind,
      parents: row.parents,
    },
  };
}

function parentFromMetricFact(fact: GovernedJoinMetricFactLike): GovernedJoinParentRef {
  return {
    family: 'governed_metric_fact',
    refId: fact.id,
    evidenceRefId: fact.evidenceRefId,
    artifactId: fact.artifactId,
    ingestJobId: fact.ingestJobId,
  };
}

function parentFromFinchSignal(signal: FinchHrisGoldSignalRow): GovernedJoinParentRef {
  const businessLineKey = readDimensionValue(signal, 'businessLineKey') ?? 'unknown';
  return {
    family: 'connector_mirror_fact',
    refId: `finch_hris_gold_signal:${signal.evidence.snapshotArtifactId}:${signal.key}:${businessLineKey}`,
    evidenceRefId: `finch:${signal.evidence.snapshotArtifactId}:${signal.evidence.inputsHash}`,
    artifactId: signal.evidence.snapshotArtifactId,
    ingestJobId: null,
    sourceFingerprint: signal.evidence.inputsHash,
  };
}

function readTemporalKind(value: unknown): GovernedJoinTemporalGrain['kind'] | null {
  if (!isRecord(value)) return null;
  const temporalGrain = value.temporalGrain;
  if (!isRecord(temporalGrain)) return null;
  return temporalGrain.kind === 'period' || temporalGrain.kind === 'point_in_time'
    ? temporalGrain.kind
    : null;
}

function readDimensionValue(signal: FinchHrisGoldSignalRow, dimensionKey: string): string | null {
  return nonEmptyString(signal.dimensions?.[dimensionKey]);
}

function compareFacts(left: GovernedJoinMetricFactLike, right: GovernedJoinMetricFactLike): number {
  return (
    String(left.businessLineKey ?? '').localeCompare(String(right.businessLineKey ?? '')) ||
    left.periodKey.localeCompare(right.periodKey) ||
    left.id.localeCompare(right.id)
  );
}

function compareSignals(left: FinchHrisGoldSignalRow, right: FinchHrisGoldSignalRow): number {
  return (
    String(left.dimensions?.businessLineKey ?? '').localeCompare(
      String(right.dimensions?.businessLineKey ?? '')
    ) || left.key.localeCompare(right.key)
  );
}

function firstDuplicate<T>(items: readonly T[], getKey: (item: T) => string): string | null {
  const seen = new Set<string>();
  for (const item of items) {
    const key = getKey(item);
    if (seen.has(key)) return key;
    seen.add(key);
  }
  return null;
}

function finiteNumber(value: unknown): number | null {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function hasNonEmptyStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.some((item) => Boolean(nonEmptyString(item)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function leftJoinKey(fact: GovernedJoinMetricFactLike): string {
  return [
    fact.tenantId,
    fact.periodType,
    fact.periodKey,
    nonEmptyString(fact.businessLineKey) ?? '',
  ].join('|');
}

function nonEmptyString(value: unknown): string | null {
  const trimmed = String(value ?? '').trim();
  return trimmed ? trimmed : null;
}

function normalizeIso(value: unknown): string | null {
  const date = new Date(String(value ?? ''));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function periodEndAsOf(periodType: string, periodKey: string | undefined): string | null {
  if (periodType !== 'fiscal_year') return null;
  const year = Number(periodKey);
  if (!Number.isInteger(year) || year < 1900 || year > 9999) return null;
  return `${String(year).padStart(4, '0')}-12-31T23:59:59.000Z`;
}

function refused<
  TReason extends GovernedJoinValidationReason | GovernedJoinRefusedResult['reason'],
>(
  reason: TReason,
  message: string,
  details?: Readonly<Record<string, unknown>>
): Extract<GovernedJoinValidationRefused | GovernedJoinRefusedResult, { reason: TReason }> {
  return {
    ok: false,
    reason,
    message,
    ...(details ? { details } : {}),
  } as Extract<GovernedJoinValidationRefused | GovernedJoinRefusedResult, { reason: TReason }>;
}
